/**
 * Sticky tab navigation and outlet (or RPA banner) for the single-order layout content column.
 * @module orderNavigationContent
 */
import React from "react";
import { Outlet } from "react-router-dom";
import SingleTab from "../../../components/singleTab/singleTab";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { isRpaInFlight } from "../utils/checkRpaError";
import { RpaStatusBanner } from "./errorComponents/rpaStatusBanner";

/** Bounding rectangle used to position sticky navigation flush with the scroll container. */
interface ContainerRect {
  left: number;
  top: number;
  width: number;
  right: number;
}

/**
 * Props for {@link OrderNavigationContent}.
 */
interface OrderNavigationContentProps {
  containerRef: React.RefObject<HTMLDivElement>;
  shouldMakeSticky: boolean;
  containerRect: ContainerRect | null;
  tabSwitchElements: Array<{ id: string; label: string }>;
  logOnTabClickEvent: (label: string) => void;
}

/**
 * Renders section tabs, optional sticky positioning, and either the child route or RPA status UI.
 *
 * @param props - Container refs, sticky layout inputs, tabs, and tab click logger
 * @returns Single-order secondary column with navigation and content
 */
const OrderNavigationContent: React.FC<OrderNavigationContentProps> = ({
  containerRef,
  shouldMakeSticky,
  containerRect,
  tabSwitchElements,
  logOnTabClickEvent,
}) => {
  const { generalOrderDetails } = useDataAccumulatorForSingleOrder();
  return (
    <div className="single-order-layout--content-outlet flex h-full w-full flex-col gap-3">
      <div
        ref={containerRef}
        className="single-order-layout--content--secondary__left flex h-full flex-col"
      >
        {shouldMakeSticky && (
          <div
            className="single-order-layout--content--secondary__left--navigation-spacer h-6 border-b border-transparent pb-9"
            aria-hidden="true"
          />
        )}
        <div
          className="single-order-layout--content--secondary__left--navigation h-6 border-b border-primaryGray-15 pb-9"
          style={{
            position: shouldMakeSticky ? "fixed" : "relative",
            top: shouldMakeSticky ? 0 : "auto",
            left: shouldMakeSticky ? containerRect?.left : "auto",
            width: shouldMakeSticky ? containerRect?.width : "auto",
            right: shouldMakeSticky ? containerRect?.right : "auto",
            zIndex: shouldMakeSticky ? 1000 : "auto",
            backgroundColor: shouldMakeSticky ? "white" : "transparent",
            paddingTop: shouldMakeSticky ? "1rem" : "0",
            boxShadow: shouldMakeSticky
              ? "0 0 10px 0 rgba(0, 0, 0, 0.1)"
              : "none",
          }}
        >
          {tabSwitchElements.map((sideNavElement) => (
            <SingleTab
              key={sideNavElement.id}
              id={sideNavElement.id}
              label={sideNavElement.label}
              logEvent={logOnTabClickEvent}
            />
          ))}
        </div>
        {isRpaInFlight(generalOrderDetails?.order) ? (
          <RpaStatusBanner />
        ) : (
          <div className="single-order-layout--content--secondary__left--outlet mt-4 h-full">
            <Outlet />
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderNavigationContent;
